import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  HourglassEmpty as PendingIcon,
  Message as MessageIcon,
  MenuBook as CourseIcon,
  Pattern as PatternIcon,
  CalendarMonth as SessionIcon,
  Quiz as QuizIcon,
  EmojiEvents as ObjectiveIcon,
  Psychology as SkillIcon,
  Image as ImageIcon,
  OndemandVideo as VideoIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { TeacherWithConnectionStatus, ExercisePattern } from '../types';
import networkService, { ContentTree, ContainerNode } from '../services/network.service';
import teacherMessageService from '../services/teacherMessage.service';
import PatternViewImportDialog from './PatternViewImportDialog';

interface TeacherDetailsModalProps {
  open: boolean;
  onClose: () => void;
  teacherId: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teacher-content-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Recursive component to render container tree
interface ContainerTreeItemProps {
  container: ContainerNode;
  level: number;
  contentType: string;
  expandedContainers: Set<number>;
  onToggle: (id: number) => void;
  onPatternClick?: (pattern: ExercisePattern) => void;
}

const ContainerTreeItem: React.FC<ContainerTreeItemProps> = ({
  container,
  level,
  contentType,
  expandedContainers,
  onToggle,
  onPatternClick,
}) => {
  const isExpanded = expandedContainers.has(container.id);
  const hasChildren = container.children.length > 0 || container.items.length > 0;

  return (
    <>
      <ListItemButton
        onClick={() => onToggle(container.id)}
        sx={{ pl: 2 + level * 2 }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {hasChildren ? (
            isExpanded ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />
          ) : (
            <FolderIcon color="disabled" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={container.title}
          primaryTypographyProps={{ fontWeight: 500 }}
        />
        {hasChildren && (
          <IconButton size="small">
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {/* Render child containers */}
          {container.children.map((child) => (
            <ContainerTreeItem
              key={`container-${child.id}`}
              container={child}
              level={level + 1}
              contentType={contentType}
              expandedContainers={expandedContainers}
              onToggle={onToggle}
              onPatternClick={onPatternClick}
            />
          ))}

          {/* Render items */}
          {container.items.map((item) => (
            <ContentItem
              key={`item-${item.id}`}
              item={item}
              level={level + 1}
              contentType={contentType}
              onPatternClick={onPatternClick}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

// Component for rendering individual content items
interface ContentItemProps {
  item: any;
  level: number;
  contentType: string;
  onPatternClick?: (pattern: ExercisePattern) => void;
}

const ContentItem: React.FC<ContentItemProps> = ({ item, level, contentType, onPatternClick }) => {
  const getItemIcon = () => {
    switch (contentType) {
      case 'courses':
        return <CourseIcon fontSize="small" />;
      case 'patterns':
        return <PatternIcon fontSize="small" />;
      case 'sessions':
        return <SessionIcon fontSize="small" />;
      case 'quizzes':
        return <QuizIcon fontSize="small" />;
      case 'objectives':
        return <ObjectiveIcon fontSize="small" />;
      case 'skills':
        return <SkillIcon fontSize="small" />;
      case 'images':
        return <ImageIcon fontSize="small" />;
      case 'videos':
        return <VideoIcon fontSize="small" />;
      default:
        return <FileIcon fontSize="small" />;
    }
  };

  const isClickable = contentType === 'patterns' && onPatternClick;
  const handleClick = () => {
    if (isClickable) {
      onPatternClick(item as ExercisePattern);
    }
  };

  const itemSx = {
    pl: 2 + level * 2,
    py: 0.5,
    cursor: isClickable ? 'pointer' : 'default',
    '&:hover': isClickable ? { backgroundColor: 'action.hover' } : {},
  };

  // For images, show a small thumbnail
  if (contentType === 'images' && (item.thumbnail_url || item.image_file_url)) {
    return (
      <ListItem sx={itemSx} onClick={handleClick}>
        <Box
          component="img"
          src={item.thumbnail_url || item.image_file_url}
          alt={item.title}
          sx={{
            width: 40,
            height: 40,
            objectFit: 'cover',
            borderRadius: 1,
            mr: 1,
          }}
        />
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    );
  }

  // For courses and patterns with thumbnails
  if ((contentType === 'courses' || contentType === 'patterns') && item.thumbnail_url) {
    return (
      <ListItem sx={itemSx} onClick={handleClick}>
        <Box
          component="img"
          src={item.thumbnail_url}
          alt={item.title}
          sx={{
            width: 50,
            height: 35,
            objectFit: 'contain',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            mr: 1,
          }}
        />
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    );
  }

  // Default item display
  return (
    <ListItem sx={itemSx} onClick={handleClick}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        {getItemIcon()}
      </ListItemIcon>
      <ListItemText
        primary={item.title || item.name}
        secondary={item.description?.substring(0, 80)}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};

const TeacherDetailsModal: React.FC<TeacherDetailsModalProps> = ({
  open,
  onClose,
  teacherId,
}) => {
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState<TeacherWithConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [startingConversation, setStartingConversation] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Content tree state - one for each content type
  const [contentTrees, setContentTrees] = useState<Record<string, ContentTree>>({});
  const [loadingContent, setLoadingContent] = useState(false);

  // Expanded containers state
  const [expandedContainers, setExpandedContainers] = useState<Set<number>>(new Set());

  // Pattern dialog state
  const [selectedPattern, setSelectedPattern] = useState<ExercisePattern | null>(null);
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);

  const contentTypes = ['courses', 'patterns', 'sessions', 'quizzes', 'objectives', 'skills', 'images', 'videos'];

  useEffect(() => {
    if (open && teacherId) {
      loadTeacherProfile();
      setTabValue(0);
      setExpandedContainers(new Set());
    } else {
      setTeacher(null);
      setContentTrees({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teacherId]);

  useEffect(() => {
    if (teacherId && open) {
      loadContentForTab(tabValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, teacherId, open]);

  const loadTeacherProfile = async () => {
    if (!teacherId) return;

    setLoading(true);
    try {
      const data = await networkService.getTeacherProfile(teacherId);
      setTeacher(data);
    } catch (error) {
      console.error('Error loading teacher profile:', error);
      toast.error('Erreur lors du chargement du profil');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const loadContentForTab = async (tab: number) => {
    if (!teacherId) return;

    const contentType = contentTypes[tab];
    if (contentTrees[contentType]) {
      // Already loaded
      return;
    }

    setLoadingContent(true);
    try {
      const tree = await networkService.getTeacherPublicContent(teacherId, contentType);
      setContentTrees(prev => ({ ...prev, [contentType]: tree }));
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleToggleContainer = (containerId: number) => {
    setExpandedContainers(prev => {
      const next = new Set(prev);
      if (next.has(containerId)) {
        next.delete(containerId);
      } else {
        next.add(containerId);
      }
      return next;
    });
  };

  const handlePatternClick = (pattern: ExercisePattern) => {
    setSelectedPattern(pattern);
    onClose(); // Close the teacher modal
    setPatternDialogOpen(true); // Open the pattern dialog
  };

  const handlePatternDialogClose = () => {
    setPatternDialogOpen(false);
    setSelectedPattern(null);
  };

  const handleSendRequest = async () => {
    if (!teacherId) return;

    setSendingRequest(true);
    try {
      await networkService.sendNetworkRequest(teacherId);
      toast.success('Demande envoyée avec succès');
      loadTeacherProfile();
    } catch (error: any) {
      console.error('Error sending request:', error);
      const message = error.response?.data?.error || 'Erreur lors de l\'envoi de la demande';
      toast.error(message);
    } finally {
      setSendingRequest(false);
    }
  };

  const handleStartConversation = async () => {
    if (!teacherId) return;

    setStartingConversation(true);
    try {
      await teacherMessageService.getOrCreateConversation(teacherId);
      onClose();
      navigate('/messages');
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setStartingConversation(false);
    }
  };

  const getConnectionButton = () => {
    if (!teacher?.connection_status) return null;

    const status = teacher.connection_status;

    if (status.is_self) {
      return <Chip label="C'est vous" color="default" icon={<PersonIcon />} size="small" />;
    }

    if (status.is_connected) {
      return <Chip label="Dans votre réseau" color="success" icon={<CheckIcon />} size="small" />;
    }

    if (status.pending_request_sent) {
      return <Chip label="Demande en attente" color="warning" icon={<PendingIcon />} size="small" />;
    }

    if (status.pending_request_received) {
      return <Chip label="Vous avez reçu une demande" color="info" icon={<PendingIcon />} size="small" />;
    }

    return (
      <Button
        variant="contained"
        size="small"
        startIcon={sendingRequest ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
        onClick={handleSendRequest}
        disabled={sendingRequest}
      >
        Ajouter à mon réseau
      </Button>
    );
  };

  const renderContentTree = (contentType: string) => {
    if (loadingContent && !contentTrees[contentType]) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    const tree = contentTrees[contentType];
    if (!tree) {
      return null;
    }

    const hasContent = tree.containers.length > 0 || tree.items.length > 0;

    if (!hasContent) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" py={4}>
          Aucun contenu public
        </Typography>
      );
    }

    return (
      <List
        dense
        sx={{
          maxHeight: 400,
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Render root containers */}
        {tree.containers.map((container) => (
          <ContainerTreeItem
            key={`container-${container.id}`}
            container={container}
            level={0}
            contentType={contentType}
            expandedContainers={expandedContainers}
            onToggle={handleToggleContainer}
            onPatternClick={contentType === 'patterns' ? handlePatternClick : undefined}
          />
        ))}

        {/* Render root items (not in any container) */}
        {tree.items.map((item) => (
          <ContentItem
            key={`item-${item.id}`}
            item={item}
            onPatternClick={contentType === 'patterns' ? handlePatternClick : undefined}
            level={0}
            contentType={contentType}
          />
        ))}
      </List>
    );
  };

  const tabs = [
    { label: 'Cours', icon: <CourseIcon fontSize="small" />, type: 'courses' },
    { label: 'Patterns', icon: <PatternIcon fontSize="small" />, type: 'patterns' },
    { label: 'Séances', icon: <SessionIcon fontSize="small" />, type: 'sessions' },
    { label: 'Quizz', icon: <QuizIcon fontSize="small" />, type: 'quizzes' },
    { label: 'Objectifs', icon: <ObjectiveIcon fontSize="small" />, type: 'objectives' },
    { label: 'Savoir-faire', icon: <SkillIcon fontSize="small" />, type: 'skills' },
    { label: 'Images', icon: <ImageIcon fontSize="small" />, type: 'images' },
    { label: 'Vidéos', icon: <VideoIcon fontSize="small" />, type: 'videos' },
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Profil enseignant</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : teacher ? (
          <Grid container spacing={3}>
            {/* Left column - Teacher info */}
            <Grid item xs={12} md={3}>
              <Box sx={{ position: 'sticky', top: 0 }}>
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                  <Avatar
                    src={teacher.profile_photo_url || undefined}
                    sx={{ width: 100, height: 100, mb: 2 }}
                  >
                    <PersonIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Typography variant="h6" align="center">
                    {teacher.first_name} {teacher.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                    @{teacher.username}
                  </Typography>
                  {getConnectionButton()}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                  <Typography variant="body2" noWrap title={teacher.email}>
                    {teacher.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right column - Content tabs */}
            <Grid item xs={12} md={9}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.label}
                      label={tab.label}
                      icon={tab.icon}
                      iconPosition="start"
                      sx={{ minHeight: 48, textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
              </Box>

              {tabs.map((tab, index) => (
                <TabPanel key={tab.label} value={tabValue} index={index}>
                  {renderContentTree(tab.type)}
                </TabPanel>
              ))}
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions>
        {teacher && teacher.connection_status?.is_connected && (
          <Button
            startIcon={startingConversation ? <CircularProgress size={20} /> : <MessageIcon />}
            onClick={handleStartConversation}
            disabled={startingConversation}
          >
            Envoyer un message
          </Button>
        )}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>

    {/* Pattern View/Import Dialog */}
    <PatternViewImportDialog
      open={patternDialogOpen}
      onClose={handlePatternDialogClose}
      pattern={selectedPattern}
      onImportSuccess={() => {
        console.log('Pattern imported successfully');
      }}
    />
  </>
  );
};

export default TeacherDetailsModal;
